// src/components/LandingPage.js

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaPhoneAlt } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;

const LandingPage = () => {
  const serviceCards = useMemo(() => [
    {
      title: 'Interior Painting',
      description: 'Professional painting for indoor surfaces with a flawless finish.',
      slug: 'interior-painting',
    },
    {
      title: 'Exterior Painting',
      description: 'Professional painting for outdoor surfaces with durable, weather-resistant finishes.',
      slug: 'exterior-painting',
    },
    {
      title: 'Wall Repair & Surface Preparation',
      description: 'Professional repair of cracks, holes, and meticulous surface preparation for optimal paint adhesion.',
      slug: 'wall-repair',
    },
    {
      title: 'Color Consultation & Matching',
      description: 'Expert advice on selecting colors that suit your style and space perfectly.',
      slug: 'color-consultation-matching',
    },
    {
      title: 'Protective Coatings',
      description: 'Durable coatings to protect your surfaces from wear, moisture, and weather damage.',
      slug: 'protective-coatings',
    },
    {
      title: 'Ceiling Designing',
      description: 'Elegant and creative ceiling designs that elevate your interior with style.',
      slug: 'ceiling-designing',
    },
  ], []);

  const [categoryBackgrounds, setCategoryBackgrounds] = useState({});
  const [currentBgIndex, setCurrentBgIndex] = useState({});

  const fetchCategoryImages = useCallback(async (slug) => {
    try {
      const res = await axios.get(`${API_BASE}/api/images?category=${slug}&limit=5`);
      setCategoryBackgrounds(prev => ({ ...prev, [slug]: res.data.map(img => img.url) }));
      setCurrentBgIndex(prev => ({ ...prev, [slug]: 0 }));
    } catch (err) {
      console.error(`Error fetching images for ${slug}:`, err.response?.data || err.message);
      setCategoryBackgrounds(prev => ({ ...prev, [slug]: [] }));
    }
  }, []);

  useEffect(() => {
    serviceCards.forEach(card => {
      fetchCategoryImages(card.slug);
    });
  }, [fetchCategoryImages, serviceCards]);

  useEffect(() => {
    const intervals = {};
    serviceCards.forEach(card => {
      if (categoryBackgrounds[card.slug] && categoryBackgrounds[card.slug].length > 1) {
        intervals[card.slug] = setInterval(() => {
          setCurrentBgIndex(prev => {
            const nextIndex = (prev[card.slug] + 1) % categoryBackgrounds[card.slug].length;
            return { ...prev, [card.slug]: nextIndex };
          });
        }, 5000);
      }
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [categoryBackgrounds, serviceCards]);

  // Gradient text style for card titles & motto
  const gradientTextStyle = {
    background: 'linear-gradient(90deg, #a5f3fc 0%, #3b82f6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 'bold',
  };

  // Solid cyan-100 color for main title
  const cyan100Color = '#cffafe';

  return (
    <div className="min-h-screen text-white font-inter bg-gray-800">
      <div className="min-h-screen flex flex-col justify-between px-4 py-6 w-full max-w-6xl mx-auto">

        {/* Header Section */}
        <div className="text-center space-y-1 mt-6">
          <h1
            className="text-5xl md:text-6xl font-extrabold font-montserrat tracking-wide"
            style={{ color: cyan100Color, textShadow: '1px 1px 2px #000' }}
          >
            SK Paints
          </h1>
          <p
            className="text-lg md:text-xl font-montserrat"
            style={gradientTextStyle}
          >
            Elegant, Modern, Long-Lasting.
          </p>
        </div>

        {/* Services Section */}
        <h2
          className="text-3xl font-bold font-montserrat text-center mt-4 mb-6"
          style={{ color: cyan100Color }}
        >
          Our Services
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceCards.map((card) => {
            const imagesForCategory = categoryBackgrounds[card.slug] || [];
            const currentImage = imagesForCategory[currentBgIndex[card.slug]] || null;

            return (
              <Link
                key={card.slug}
                to={`/gallery?category=${card.slug}`}
                className="relative h-60 md:h-72 lg:h-80 overflow-hidden rounded-3xl shadow-2xl border border-white/20 transition duration-300 hover:shadow-3xl bg-center bg-cover flex flex-col text-center bg-white bg-opacity-10 cursor-pointer"
                style={{
                  backgroundImage: currentImage ? `url(${currentImage})` : 'none',
                  backgroundColor: currentImage ? 'rgba(0, 0, 0, 0.2)' : 'rgba(23, 37, 46, 0.5)',
                  backgroundBlendMode: currentImage ? 'multiply' : 'normal'
                }}
              >
                <div className="absolute inset-0 bg-black/40 rounded-3xl"></div>
                <div className="relative z-10 p-6 space-y-2 flex flex-col flex-grow justify-center items-center">
                  <h3 className="text-2xl font-bold font-montserrat" style={gradientTextStyle}>
                    {card.title}
                  </h3>
                  <p
                    className="text-base md:text-[17px] font-montserrat leading-snug text-cyan-300 font-semibold"
                  >
                    {card.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Signup & Login Buttons */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-10">
          <Link to="/signup" className="bg-white text-cyan-700 hover:bg-gray-200 py-2 px-6 rounded-full font-semibold w-48 text-center">
            Sign Up
          </Link>
          <Link to="/login" className="bg-white text-cyan-700 hover:bg-gray-200 py-2 px-6 rounded-full font-semibold w-48 text-center">
            Login
          </Link>
        </div>

        {/* Quick Links */}
        <div className="flex justify-center flex-wrap gap-6 text-cyan-200 font-medium mt-6">
          <Link to="/appointment" className="hover:underline">Book Appointment</Link>
          <Link to="/feedback" className="hover:underline">Send Feedback</Link>
        </div>

        {/* Footer */}
        <div className="text-sm mt-10 mb-2 text-center space-y-2 text-cyan-100">
          <div className="flex justify-center items-center gap-6 flex-wrap">
            <a href="tel:+256775142825" className="flex items-center gap-2 text-white hover:text-gray-300">
              <FaPhoneAlt />
              <span>+256 775142825</span>
            </a>
            <a href="https://wa.me/256708662581" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-gray-300">
              <FaWhatsapp />
              <span>+256 708662581</span>
            </a>
          </div>
          <div className="flex justify-center items-center gap-2 text-white">
            <MdEmail />
            <a href="mailto:skpaints001@gmail.com" className="underline">skpaints001@gmail.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
